import type { ItemFieldRow } from './item-field-reader';
import { readInventoryField } from './inventory-raw-readers';
import { phpJsonInteger } from './php-integer-arithmetic';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { appendCompiledLayerInPlace, appendCompiledStatusRowInPlace, mergeStatusOriginAbilityLayers } from './status-row-construction';
import type { StatusOriginLayerReaders } from './status-row-construction';
import type { StatusReferenceResolvers } from './status-reference-resolution';
import { isHostileAuraSelfAnchor, isDormantOwnerGatedAura, type AuraOwnerStateResolver } from './creature-status-reader';
import { isSelfStatusOrigin } from '../statuses/status-origin';
import { statusLayerIsResidual, statusLayerPayloadRetired } from './status-residue-layers';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const has = (value: unknown, key: string): boolean => arrayLike(value) && Object.prototype.propertyIsEnumerable.call(value, key);
const own = (value: unknown, key: string): unknown => has(value, key) ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
const validUuid = (value: unknown): value is string => typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(value);
const upperAscii = (value: string): string => value.replace(/[a-z]/g, letter => letter.toUpperCase());
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key], i) => key === String(i)) ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, clone(child)]));
}
function stringArgument(value: unknown): string {
    if (value == null || arrayLike(value)) throw new TypeError('Status mode requires a scalar string');
    return phpJsonString(value);
}
function zeroKey(value: unknown): unknown {
    return arrayLike(value) ? own(value, '0') ?? '' : typeof value === 'string' ? value[0] ?? '' : '';
}
const normalizedGrantId = (value: unknown): string => {
    const id = arrayLike(value) ? own(value, '0') ?? '' : value;
    return typeof id === 'string' ? id : '';
};
const looseKnownString = (value: unknown, expected: string): boolean => value === expected || value === true;

export class MissingStatusSourceColumn extends Error {
    constructor(public readonly column: string) { super(`Unavailable status source column: ${column}`); this.name = 'MissingStatusSourceColumn'; }
}
function required(row: ItemFieldRow, key: string): unknown {
    if (!has(row, key)) throw new MissingStatusSourceColumn(key);
    return own(row, key);
}
export interface StatusLayeringMetadata {
    statusIds: Readonly<Record<string, string>>;
    dynamicLayers: readonly string[];
    integerWalkLayers: readonly string[];
    presenceDefenseLayers: readonly string[];
    /** FT_STATUS_STACK_SCALED_RESIDUE_LAYERS + every quantity-group status id (base and alias) — ft_status_row_scale_residue_fragment.
     *  Optional: a contract without them scales nothing (the pre-2026-10-07 twin). */
    stackScaledResidueLayers?: readonly string[];
    quantityIds?: readonly string[];
    contestMarker: string;
    teamIds: { friend: string; enemy: string; neutral: string };
}
export interface StatusLayeringReaders extends StatusOriginLayerReaders {
    resolveAuraOwner?: AuraOwnerStateResolver;
    itemField: (row: ItemFieldRow | null, key: string) => unknown;
    layerValue: (row: ItemFieldRow | null, key: string) => unknown;
    references: StatusReferenceResolvers;
    prefetch: (mode: string, id: string) => ItemFieldRow | null;
    fetchStatuses: (filters: ItemFieldRow) => ItemFieldRow;
    prefetchHit?: () => void;
    fetchCompleted?: () => void;
    itemRow: (id: string) => unknown;
    fullItem: (id: string, suppress: boolean) => ItemFieldRow | null;
    dynamicItem: (id: string, context: string) => ItemFieldRow;
    itemName: (id: string) => unknown;
    campaignContext?: (row: ItemFieldRow) => ItemFieldRow | null;
    campaignSeconds: (time: ItemFieldRow) => unknown;
    casterTeam: (id: unknown) => unknown;
    warning: (message: string) => void;
    log: (message: string) => void;
}
/** The read-only half of the request prefetch; covered false still satisfies isset. */
export function readStatusPrefetch(prefetch: ItemFieldRow | null, mode: string, rawId: string): ItemFieldRow | null {
    const id = upperAscii(rawId);
    if (!arrayLike(prefetch) || (mode !== 'target' && mode !== 'caster') || id === '' || own(own(prefetch, 'covered'), id) == null) return null;
    const value = own(own(prefetch, mode), id) ?? [];
    if (!arrayLike(value)) throw new TypeError('Status prefetch returned a non-array');
    return clone(value) as ItemFieldRow;
}
function flatField(row: ItemFieldRow | null, key: string): unknown {
    if (row !== null && !arrayLike(row)) throw new TypeError('Encounter field requires a PHP array or null');
    let value: unknown = own(row, key) ?? null;
    if (arrayLike(value) && has(value, '0')) value = own(value, '0');
    const shellId = own(own(value, 'items'), 'item_id');
    return typeof shellId === 'string' ? shellId : value;
}
function campaignCalendar(row: ItemFieldRow): unknown {
    const bucket = own(row, 'campaigns');
    return flatField(has(bucket, 'calendar_id') ? bucket as ItemFieldRow : row, 'calendar_id');
}
function carrierTeam(row: ItemFieldRow, readers: StatusLayeringReaders): string | null {
    const value = flatField((own(row, 'encounter_creatures') ?? null) as ItemFieldRow | null, 'team')
        ?? flatField(row, 'team') ?? readers.layerValue(row, 'team') ?? null;
    return value === null || value === '' || arrayLike(value) ? null : phpJsonString(value);
}
function hostile(caster: unknown, target: unknown, ids: StatusLayeringMetadata['teamIds']): boolean {
    if (!phpJsonTruthy(caster) || !phpJsonTruthy(target)) return false;
    if (looseKnownString(caster, ids.friend)) return looseKnownString(target, ids.enemy);
    if (looseKnownString(caster, ids.enemy)) return looseKnownString(target, ids.friend);
    return looseKnownString(caster, ids.neutral) && looseKnownString(target, ids.enemy);
}
const activeBuckets = ['ability', 'full_ability', 'quick_ability', 'attack_ability', 'free_ability', 'reaction_ability', 'opportunity_attack_ability', 'passive_ability'];
const dynamicAbilityBuckets = new Set([...activeBuckets, 'curse', 'sanguine', 'menta', 'innate_spell_per_long_rest', 'at_will_spell']);
const retiredLayers = ['ac_mod_environmental', 'if_caster_has_status_effect_not_active', 'roll_type_against_dis', 'roll_type_dis',
    'speed_burrowing', 'speed_climbing', 'speed_flying', 'speed_hovering', 'speed_swimming', 'speed_walking'];
const timeParts = ['year', 'month', 'day', 'hour', 'minute', 'second'];

/** Activity display fields for one saved target status, shared by cold compilation and held-row deltas. */
export function statusActivityFields(raw: ItemFieldRow, origin: ItemFieldRow, campaign: () => ItemFieldRow | null,
    metadata: Pick<StatusLayeringMetadata, 'statusIds'>,
    readers: Pick<StatusLayeringReaders, 'layerValue' | 'itemName' | 'campaignSeconds'>): Record<string, unknown> {
    const statusItemId = own(raw, 'status_item_id');
    if (!validUuid(statusItemId)) return {};
    const kind = ['gathering', 'reading', 'training'].find(kind => upperAscii(statusItemId) === upperAscii(metadata.statusIds[kind] ?? ''));
    if (!kind) return {};
    let skillId = kind === 'training' ? own(raw, 'status_origin_id') : readers.layerValue(origin,
        kind === 'reading' ? 'skill_trained' : 'skill_roll_contester');
    if (arrayLike(skillId)) skillId = own(skillId, '0') ?? '';
    const skill = validUuid(skillId) ? skillId : '', name = skill !== '' ? readers.itemName(skill) ?? '' : '';
    const context = campaign(), calendar = arrayLike(context) ? campaignCalendar(context) : null;
    const time = (prefix: 'starts' | 'ends'): ItemFieldRow => ({ ...Object.fromEntries(timeParts.map(part =>
        ['campaign_time_' + part, own(raw, 'status_' + prefix + '_campaign_' + part) ?? 0])), calendar_id: calendar });
    return { activity_kind: kind, activity_skill_id: skill, activity_skill_name: name,
        activity_start_seconds: readers.campaignSeconds(time('starts')), activity_end_seconds: readers.campaignSeconds(time('ends')) };
}

/** Full layer_on_status_effects producer over explicit source/query inputs.
 * The mutable working root is private; append helpers never write to the caller.
 * No persistent status apply/remove/upsert path is invoked here.
 */
export function layerStatusesOntoCarrier(target: ItemFieldRow, rawMode: unknown, explicitRows: unknown,
    metadata: StatusLayeringMetadata, readers: StatusLayeringReaders): ItemFieldRow {
    if (!arrayLike(target)) throw new TypeError('Status carrier requires a PHP array');
    const mode = stringArgument(rawMode);
    if (explicitRows != null && !arrayLike(explicitRows)) throw new TypeError('Prefetched statuses require a PHP array or null');
    const out = Object.fromEntries(Object.entries(clone(target) as ItemFieldRow));
    const result = (): ItemFieldRow => clone(out) as ItemFieldRow;
    if (mode !== 'target' && mode !== 'caster') {
        readers.warning("Invalid mode for layer_on_status_effects: must be 'target' or 'caster'"); return result();
    }
    const inventoryId = readInventoryField(out, 'inventory_id'), encounterId = own(out, 'encounter_id');
    const inventoryInstance = phpJsonTruthy(inventoryId);
    // ft_status_row_scale_residue_fragment: a QUANTITY status's integer-literal residue × its stack (Maimed x3 → "-3", Mirror Images x2 →
    // "+6"); the PHP twin reads status_item_id, then the row's item_id
    const scaleResidue = (raw: ItemFieldRow, statusItemId: unknown, layer: string, value: unknown): unknown => {
        if (!(metadata.stackScaledResidueLayers ?? []).includes(layer)) return value;
        const text = typeof value === 'number' && Number.isInteger(value) ? String(value) : typeof value === 'string' ? value.trim() : null;
        if (text === null || !/^[+-]?\d+$/.test(text)) return value;
        const sid = phpJsonString(statusItemId ?? readers.itemField(raw, 'item_id') ?? '');
        if (sid === '' || !(metadata.quantityIds ?? []).some(id => upperAscii(id) === upperAscii(sid))) return value;
        let rawStack = own(raw, 'status_stack') ?? 1; if (arrayLike(rawStack)) rawStack = own(rawStack, '0') ?? 1;
        const stack = Math.max(1, Number(phpJsonInteger(rawStack)));
        if (stack <= 1) return value;
        const scaled = Number(text) * stack;
        return typeof value === 'number' ? scaled : (text[0] === '+' && scaled > 0 ? '+' + scaled : String(scaled));
    };
    let id: unknown = inventoryId ?? own(own(out, 'encounter_creatures'), 'encounter_creature_id')
        ?? (arrayLike(encounterId) ? own(encounterId, '0') : encounterId) ?? null;
    if (!phpJsonTruthy(id) && (readers.itemField(out, 'item_type') ?? '') === 'status_effect'
        && phpJsonTruthy(own(out, 'status_id')) && validUuid(own(out, 'status_id'))) id = own(out, 'status_id');
    if (!phpJsonTruthy(id)) {
        const type = readers.itemField(out, 'item_type') ?? '';
        readers.log(`layer_on_status_effects: no id anchor (item_type=${phpJsonTruthy(type) ? phpJsonString(type) : 'unknown'}, mode=${mode})`);
        return result();
    }
    let statuses: ItemFieldRow;
    if (explicitRows != null) statuses = explicitRows as ItemFieldRow;
    else {
        const prefetched = readers.prefetch(mode, phpJsonString(id));
        if (prefetched !== null) { statuses = prefetched; readers.prefetchHit?.(); }
        else { statuses = readers.fetchStatuses({ status_deleted: 0, [mode === 'target' ? 'status_target_id' : 'status_caster_id']: id }); readers.fetchCompleted?.(); }
    }
    for (const raw of Object.values(statuses)) {
        const statusItemId = own(raw, 'status_item_id') ?? '', joined = zeroKey(own(raw, 'inventory_item_id'));
        let effect = readers.references.effect(statusItemId, joined);
        if (statusLayerPayloadRetired(phpJsonString(statusItemId), metadata.statusIds)) {
            const edited = Object.fromEntries(Object.entries(effect)); edited.item_layer_types = [];
            for (const name of retiredLayers) delete edited[name]; effect = edited;
        }
        const originId = own(raw, 'status_origin_id') ?? '';
        let origin = isSelfStatusOrigin(own(raw, 'status_id'), originId) ? []
            : readers.references.origin(originId, 'function: layer_on_status_effects');
        if (!arrayLike(raw)) throw new TypeError('Contest status marker requires a PHP array');
        const targets = own(raw, 'status_casting_targets_arr') ?? '';
        const marker = (arrayLike(targets) ? own(targets, '0') ?? '' : targets) === metadata.contestMarker;
        if (marker) {
            effect = { items: { item_id: phpJsonString(statusItemId), item_name: 'Contest Success Immunity', item_type: 'status_effect' }, item_layer_types: [] };
            origin = [];
        }
        // Hostile-aura self-anchor (PHP twin: layer_on_status_effects): keep the row, merge no payload.
        const selfAnchor = !marker && isHostileAuraSelfAnchor(effect, own(raw, 'status_aoe_source') ?? 0,
            own(raw, 'status_caster_id') ?? '', own(raw, 'status_target_id') ?? '', readers.layerValue);
        const suppress = marker || selfAnchor || isDormantOwnerGatedAura(
            { ...Object.fromEntries(Object.entries(raw)), status_arr: effect }, readers.layerValue, readers.resolveAuraOwner, out);
        let layerEffect: ItemFieldRow = suppress ? [] : effect;
        // Item-targeted origin (PHP twin: layer_on_status_effects): its payload belongs to the item it
        // was cast on, so a creature carrier never merges it (Frostwind Blade via Concentrating).
        const originMergeBlocked = !inventoryInstance && phpJsonTruthy(origin) && arrayLike(origin)
            && readers.hasLayer(origin, 'ability_works_only_against_item_type');
        if (!suppress && !originMergeBlocked && phpJsonTruthy(origin)) layerEffect = mergeStatusOriginAbilityLayers(layerEffect, origin, readers);
        const activity = mode === 'target' ? statusActivityFields(raw, origin, () => readers.campaignContext?.(out) ?? null, metadata, readers) : {};
        // Maintain source read order: missing raw columns are not inferred from a lean row.
        const entry: Record<string, unknown> = { status_id: required(raw, 'status_id'), status_private: required(raw, 'status_private') };
        for (const part of timeParts) entry['status_paused_campaign_' + part] = own(raw, 'status_paused_campaign_' + part) ?? null;
        Object.assign(entry, { status_arr: effect, status_caster_id: required(raw, 'status_caster_id'), status_target_id: required(raw, 'status_target_id'),
            status_shapeshift_monster_template_id: own(raw, 'status_shapeshift_monster_template_id') ?? null,
            status_casting_targets_arr: required(raw, 'status_casting_targets_arr'), status_origin_point_x: required(raw, 'status_origin_point_x'),
            status_origin_point_y: own(raw, 'status_origin_point_y') ?? 0, status_origin_point_z: required(raw, 'status_origin_point_z'),
            status_distance_point_x: required(raw, 'status_distance_point_x'), status_distance_point_y: own(raw, 'status_distance_point_y') ?? 0,
            status_distance_point_z: required(raw, 'status_distance_point_z'), status_aoe_type: own(raw, 'status_aoe_type') ?? '',
            status_aoe_distance: required(raw, 'status_aoe_distance'), status_aoe_distance_depth: required(raw, 'status_aoe_distance_depth'),
            status_aoe_distance_height: required(raw, 'status_aoe_distance_height'), status_aoe_source: own(raw, 'status_aoe_source') ?? 0,
            status_aoe_geometry: own(raw, 'status_aoe_geometry') ?? null,
            status_damage_type: own(raw, 'status_damage_type') ?? '', status_forced_roll_extreme: Number(phpJsonInteger(own(raw, 'status_forced_roll_extreme') ?? 0)),
            status_stack: Number(phpJsonInteger(own(raw, 'status_stack') ?? 1) < 1n ? 1n : phpJsonInteger(own(raw, 'status_stack') ?? 1)),
            status_origin_id: originId, status_origin_id_item_name: [readers.itemField(origin, 'item_name') ?? ''],
            status_origin_id_item_type: [readers.itemField(origin, 'item_type') ?? ''], status_origin_arr: origin });
        for (const key of ['status_connected_id', 'status_campaign_id', ...['starts', 'ends'].flatMap(prefix => timeParts.map(part => 'status_' + prefix + '_campaign_' + part)),
            'status_ends_on_target_start_of_turn', 'status_ends_on_target_end_of_turn', 'status_ends_on_caster_start_of_next_turn', 'status_ends_on_caster_end_of_next_turn']) entry[key] = required(raw, key);
        appendCompiledStatusRowInPlace(out, mode, { ...entry, ...activity });
        const grant = (bucket: string, abilityId: unknown, ability: ItemFieldRow): void => appendCompiledLayerInPlace(out, bucket, {
            ability_id: abilityId, item_name: readers.itemField(ability, 'item_name'), source: required(raw, 'status_id'), requires_level: 0,
            item_arr: ability, built_from_item_type: 'status_effect', built_from_id: required(raw, 'status_id'),
        });
        if (mode === 'target' && !suppress && phpJsonTruthy(effect) && phpJsonTruthy(origin)
            && (phpJsonTruthy(readers.layerValue(origin, 'roll_random_table')) || phpJsonTruthy(readers.layerValue(origin, 'contingent_ability')))) {
            const type = readers.itemField(effect, 'item_type'), caster = normalizedGrantId(own(raw, 'status_caster_id') ?? ''), targetId = normalizedGrantId(own(raw, 'status_target_id') ?? '');
            const self = caster !== '' && upperAscii(caster) === upperAscii(targetId);
            if (typeof type === 'string' && activeBuckets.includes(type) && (type !== 'passive_ability' || self)) grant(type, statusItemId, effect);
        }
        if (mode !== 'target') continue;
        for (const layer of metadata.dynamicLayers) {
            const values = readers.layerEntries(layerEffect, layer);
            if (!phpJsonTruthy(values) || looseKnownString(readers.itemField(effect, 'item_type') ?? '', 'creature')) continue;
            for (const value of Object.values(values)) {
                if (dynamicAbilityBuckets.has(layer)) {
                    let ability: ItemFieldRow = [];
                    if (validUuid(value)) {
                        if (phpJsonTruthy(readers.itemRow(value))) ability = readers.fullItem(value, false) ?? [];
                        else {
                            const inventory = readers.dynamicItem(value, 'function: layer_on_status_effects ability');
                            const template = readInventoryField(inventory, 'inventory_item_id') ?? '';
                            if (validUuid(template) && phpJsonTruthy(readers.itemRow(template))) ability = readers.fullItem(template, false) ?? [];
                        }
                    }
                    const team = carrierTeam(out, readers);
                    if (phpJsonTruthy(readers.layerValue(ability, 'buff_does_not_layer_onto_opposing_team')) && phpJsonTruthy(team)
                        && hostile(readers.casterTeam(required(raw, 'status_caster_id')), team, metadata.teamIds)) continue;
                    if (phpJsonTruthy(ability)) grant(layer, value, ability);
                } else if (statusLayerIsResidual(layer, metadata, inventoryInstance))
                    appendCompiledLayerInPlace(out, layer, scaleResidue(raw, statusItemId, layer, value));
            }
        }
    }
    return result();
}
